/**
 * Module registry: data source → dashboard mapping.
 *
 * Imported by both @prism/web (to build sidebar nav and render pages)
 * and @prism/api (to determine which data to query and insights to generate).
 *
 * To add a new data source: register a module here and the dashboard adapts
 * automatically. No code changes required in the UI or routing layer.
 */

export const modules = {
  website_analytics: {
    sources: ["ga4", "adobe_analytics", "matomo"],
    enables: ["overview", "traffic", "conversions"],
    metrics: ["sessions", "users", "bounce_rate", "engaged_sessions", "events"],
    insights: ["main_insight", "hourly_insight", "notable_sources"],
  },
  search_ads: {
    sources: ["google_ads", "bing_ads"],
    enables: ["ads"],
    metrics: ["clicks", "impressions", "spend", "cpc", "conversions", "keywords"],
    insights: ["keyword_insight", "geo_insight"],
  },
  social_ads: {
    sources: ["meta_ads", "linkedin_ads", "tiktok_ads"],
    enables: ["ads"],
    metrics: ["reach", "impressions", "spend", "cpm", "cpc", "conversions"],
    insights: ["audience_insight", "creative_insight"],
  },
  ecommerce: {
    sources: ["shopify", "woocommerce", "bigcommerce", "square", "stripe"],
    enables: ["sales"],
    metrics: ["orders", "revenue", "aov", "products", "channels"],
    insights: ["channel_opportunity", "buyer_behavior", "pre_call_briefing"],
  },
  crm: {
    sources: ["hubspot", "salesforce", "pipedrive"],
    enables: ["leads"],
    metrics: ["leads", "pipeline_value", "conversion_rate", "deal_stages"],
    insights: ["pipeline_insight", "lead_quality"],
  },
  email_marketing: {
    sources: ["mailchimp", "klaviyo", "constant_contact"],
    enables: ["email"],
    metrics: ["sends", "opens", "clicks", "unsubscribes", "revenue_attributed"],
    insights: ["email_performance", "list_health"],
  },
  social_media: {
    // "metricool" is the social provider (2026-09-04): one BMG-level token,
    // one Metricool brand per client on DataSource.config.blogId. Adding a
    // metricool data source enables the Social tab via the same source→page
    // model as everything else. (CoSchedule retired.)
    sources: ["meta_business", "linkedin_pages", "google_business_profile", "metricool"],
    enables: ["social"],
    metrics: ["followers", "engagement", "reviews", "posts"],
    insights: ["engagement_insight", "review_insight"],
  },
  call_tracking: {
    sources: ["callrail", "calltrackingmetrics"],
    enables: ["calls"],
    metrics: ["calls", "call_duration", "call_source"],
    insights: ["call_attribution", "call_quality"],
  },
  seo: {
    sources: ["google_search_console"],
    enables: ["seo"],
    metrics: ["clicks", "impressions", "ctr", "position", "queries"],
    insights: ["ranking_changes"],
  },
  ai_visibility: {
    // Empty sources: this module doesn't depend on an external data platform.
    // It runs against LLM APIs (Claude/OpenAI/Perplexity) using the client's
    // own business info. Every client gets it active by default — see the
    // empty-sources branch in activeModulesFor() below.
    sources: [],
    enables: ["ai-visibility"],
    metrics: ["mentions", "visibility_score", "engines_tested"],
    insights: ["visibility_summary", "improvement_recommendations"],
  },
};

/**
 * Canonical dashboard page definitions. Pages appear in the sidebar only if
 * at least one active module enables them. Order matters — this is render order.
 */
export const pages = [
  { key: "overview",      label: "Overview",       icon: "home",    group: "main" },
  { key: "traffic",       label: "Traffic",        icon: "bar",     group: "main" },
  { key: "ads",           label: "Ad Performance", icon: "target",  group: "main" },
  { key: "conversions",   label: "Conversions",    icon: "funnel",  group: "main" },
  { key: "sales",         label: "Sales Briefing", icon: "cart",    group: "main" },
  { key: "leads",         label: "Leads",          icon: "users",   group: "main" },
  { key: "email",         label: "Email",          icon: "mail",    group: "main" },
  { key: "social",        label: "Social",         icon: "hash",    group: "main" },
  { key: "calls",         label: "Calls",          icon: "phone",   group: "main" },
  { key: "seo",           label: "SEO",            icon: "search",  group: "main" },
  { key: "ai-visibility", label: "AI Visibility",  icon: "sparkle", group: "main" },
  // Settings group. "profile" is the logged-in user's own account (name, role,
  // password) — always relevant. "account" (Client Settings) is the *viewed*
  // client's business/data/billing. Order here is render order: My Profile first.
  { key: "profile",       label: "My Profile",     icon: "user",    group: "account" },
  { key: "account",       label: "Client Settings", icon: "cog",    group: "account" },
];

/**
 * Employee data-scope model (Phase 3b). An EMPLOYEE sees Overview + Traffic
 * for any client in their clientScopes (the GA4 baseline is always granted);
 * these scopes unlock additional pages. ga4 is implicit/always-on so it is not
 * a selectable scope. Imported by the API (endpoint gating) and web (nav).
 */
export const DATA_SCOPES = ["ads", "sales", "seo", "social", "email"];

const SCOPE_PAGES = {
  ads:    ["ads"],
  sales:  ["sales"],
  seo:    ["seo"],
  social: ["social"],
  email:  ["email"],
};

/**
 * Page keys an EMPLOYEE with the given dataScopes may access. Overview and
 * Traffic are always included; everything else requires the matching scope.
 * Conversions, Leads, Calls, and AI Visibility are intentionally NOT employee-
 * facing (see Phase 3b spec) and never appear here.
 */
export function employeeVisiblePages(dataScopes = []) {
  const allowed = new Set(["overview", "traffic"]);
  for (const s of dataScopes || []) (SCOPE_PAGES[s] || []).forEach((p) => allowed.add(p));
  return allowed;
}

/**
 * Given a list of connected source type strings (e.g. ["ga4", "shopify"]),
 * return the set of active module keys. Used by both frontend and backend.
 */
export function activeModulesFor(connectedSources) {
  const connected = new Set(connectedSources);
  const active = new Set();
  for (const [moduleKey, config] of Object.entries(modules)) {
    // Empty sources means the module doesn't need an external data platform
    // (e.g., ai_visibility runs on AI APIs + client info alone). Always active.
    const alwaysOn = config.sources.length === 0;
    if (alwaysOn || config.sources.some((src) => connected.has(src))) {
      active.add(moduleKey);
    }
  }
  return [...active];
}

/**
 * Given a list of active module keys, return the dashboard pages that should render.
 * The "account" page is always included.
 */
export function pagesFor(activeModuleKeys) {
  const enabled = new Set();
  for (const key of activeModuleKeys) {
    const config = modules[key];
    if (!config) continue;
    config.enables.forEach((page) => enabled.add(page));
  }
  return pages.filter((p) => p.group === "account" || enabled.has(p.key));
}

/**
 * Convenience: go straight from connected sources to rendered pages.
 */
export function pagesForSources(connectedSources) {
  return pagesFor(activeModulesFor(connectedSources));
}

/**
 * Derive a client's business-type context from their active modules.
 * Used by both the dashboard UI (to conditionally render sections — e.g.
 * hide form-activity cards for e-commerce clients where form events are
 * mostly contact-form noise) and the AI insight generator (to pick
 * prompts that match the client's business model).
 *
 * Semantics:
 *   - isEcommerce:       has Shopify/Woo/etc. connected — funnel is
 *                        product-view → cart → checkout → purchase
 *   - hasAds:            has a paid-advertising source connected
 *   - hasLeadGenForms:   has CRM or call-tracking connected — business
 *                        runs on form submissions / phone leads
 *   - isServiceBusiness: has lead-gen infra AND is NOT ecommerce —
 *                        "Conversions" page should emphasize form
 *                        activity instead of cart funnels
 *
 * A client can be BOTH ecommerce and lead-gen (e.g., a shop that also
 * runs a catering intake form). The frontend is free to show both sets
 * of sections in that case; the flags just capture the defaults.
 */
export function clientContextFromSources(connectedSources) {
  const active = new Set(activeModulesFor(connectedSources));
  const isEcommerce    = active.has("ecommerce");
  const hasLeadGenForms = active.has("crm") || active.has("call_tracking");
  return {
    isEcommerce,
    hasAds: active.has("search_ads") || active.has("social_ads"),
    hasLeadGenForms,
    isServiceBusiness: hasLeadGenForms && !isEcommerce,
  };
}
